import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { PageHeader, Card } from "@/components/shared";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Check, Copy, Target } from "lucide-react";
import { toast } from "sonner";

const ragColor = (r) => r === "green" ? "#059669" : r === "amber" ? "#C9A24B" : r === "red" ? "#DC2626" : "#64748B";
const monthStr = (d) => d.toISOString().slice(0, 7);

export default function MemberGoals() {
  const { team } = useApp();
  const [person, setPerson] = useState("");
  const [month, setMonth] = useState(monthStr(new Date()));
  const [data, setData] = useState(null);
  const [edits, setEdits] = useState({});

  useEffect(() => { if (!person && team.length) setPerson(team.find((t) => t.role !== "Admin")?.name || team[0].name); }, [team, person]);

  const load = useCallback(async () => {
    if (!person) return;
    try { const res = await api.get("/member-goals/scorecard", { params: { person, month } }); setData(res.data); setEdits({}); }
    catch { toast.error("Load failed"); }
  }, [person, month]);
  useEffect(() => { load(); }, [load]);

  const saveTarget = async (item) => {
    const val = edits[item.kpi];
    if (val === undefined) return;
    try {
      if (item.goal_id) await api.put(`/member-goals/${item.goal_id}`, { monthly_target: Number(val) });
      else await api.post("/member-goals", { person, role: data.role, kpi: item.kpi, month, monthly_target: Number(val) });
      toast.success(`${item.label} target saved`); load();
    } catch { toast.error("Save failed"); }
  };

  const copyLast = async () => {
    const d = new Date(month + "-01"); d.setMonth(d.getMonth() - 1);
    try { const res = await api.post("/member-goals/copy", null, { params: { person, from_month: monthStr(d), to_month: month } });
      toast.success(`Copied ${res.data.copied} targets from last month`); load(); }
    catch { toast.error("Copy failed"); }
  };

  return (
    <div>
      <PageHeader title="Team Goals & Targets" subtitle="Admin monthly target set kare — tool auto daily/weekly me divide karega. Achieved vs Target + RAG status live.">
        <Button data-testid="copy-last-month" onClick={copyLast} variant="outline" className="bg-transparent border-white/10 text-slate-200 hover:bg-[#1E293B] hover:text-white">
          <Copy className="h-4 w-4 mr-2" /> Copy Last Month
        </Button>
      </PageHeader>

      <div className="flex flex-wrap gap-3 mb-6">
        <Select value={person} onValueChange={setPerson}>
          <SelectTrigger data-testid="goals-person" className="w-56 bg-[#1E293B] border-white/10 text-white"><SelectValue placeholder="Select person" /></SelectTrigger>
          <SelectContent className="bg-[#1E293B] border-white/10 text-white">
            {team.filter((t) => t.role !== "Admin").map((t) => <SelectItem key={t.id} value={t.name}>{t.name} · {t.role}</SelectItem>)}
          </SelectContent>
        </Select>
        <Input data-testid="goals-month" type="month" value={month} onChange={(e) => setMonth(e.target.value)} className="w-44 bg-[#1E293B] border-white/10 text-white" />
        {data && <span className="text-xs text-slate-500 self-center">Working days: <span className="text-slate-300 font-mono-ync">{data.items[0]?.working_days || "—"}</span></span>}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {data?.items.map((item) => {
          const changed = edits[item.kpi] !== undefined && Number(edits[item.kpi]) !== item.monthly_target;
          return (
            <Card key={item.kpi} data-testid="member-goal-card" className="p-5">
              <div className="flex items-start justify-between gap-4">
                <div className="flex items-center gap-2">
                  <span className="h-2 w-2 rounded-full" style={{ background: ragColor(item.rag) }} data-testid={`mg-rag-${item.kpi}`} />
                  <div>
                    <p className="text-white text-sm">{item.label}</p>
                    <p className="text-xs text-slate-500 mt-0.5">Daily <span className="font-mono-ync text-slate-300">{item.daily_target}</span> · Weekly <span className="font-mono-ync text-slate-300">{item.weekly_target}</span></p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Input data-testid={`mg-input-${item.kpi}`} type="number" min={0}
                    value={edits[item.kpi] ?? item.monthly_target}
                    onChange={(e) => setEdits((p) => ({ ...p, [item.kpi]: e.target.value }))}
                    className="w-28 bg-[#0F172A] border-white/10 text-white font-mono-ync text-center" />
                  <Button data-testid={`mg-save-${item.kpi}`} size="icon" onClick={() => saveTarget(item)} disabled={!changed}
                    className={`h-9 w-9 ${changed ? "bg-[#059669] hover:bg-[#047857]" : "bg-[#1E293B] text-slate-600"}`}><Check className="h-4 w-4" /></Button>
                </div>
              </div>
              <div className="mt-3 pt-3 border-t border-white/5 flex items-center gap-4 text-xs">
                <span className="text-slate-400">Month <span className="font-mono-ync" style={{ color: ragColor(item.rag) }}>{item.achieved_month}</span> / {item.monthly_target || "—"}</span>
                <span className="text-slate-500">Today <span className="font-mono-ync text-slate-300">{item.achieved_today}</span></span>
                <span className="text-slate-500">Week <span className="font-mono-ync text-slate-300">{item.achieved_week}</span></span>
                {item.monthly_target > 0 && <span className="ml-auto font-mono-ync" style={{ color: ragColor(item.rag) }}>{item.pct}%</span>}
              </div>
            </Card>
          );
        })}
        {data && data.items.length === 0 && <p className="text-slate-500 text-sm">No KPIs for this role.</p>}
      </div>
    </div>
  );
}
